import React, { useCallback, useEffect, useMemo } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { AgGridReact, Grid, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import {
  ComplianceCaseTypeEnum as CaseType,
  ComplianceCaseSubStatus,
} from "~/graphql/supergraphEnums";
import { useDeleteEscalationMutation, useGetArenaCountQuery } from "~/graphql/supergraphHooks";
import { useReusableProfilesFeatureFlag } from "~/pages/Compliance/flags";
import { ComplianceCase } from "~/pages/Compliance/types";
import { isStandaloneCaseType } from "~/pages/Compliance/utils";
import { ExpandableTextPanel } from "./ExpandableTextPanel";
import { useImpactedGPContext } from "./ImpactedGPGrid/impactedGPcontext";
import * as styles from "./styles_vanilla.css";
import { allowDeleteEscalation } from "./utils";

interface IssueGridProps {
  complianceCase: ComplianceCase;
  assigneeRole?: "maker" | "checker";
}

export const IssueGrid: React.FC<IssueGridProps> = ({ complianceCase, assigneeRole }) => {
  const isReusableProfileEnabled = useReusableProfilesFeatureFlag();
  const { setCount } = useImpactedGPContext();
  const escalationDetail =
    complianceCase?.escalations?.map((escalation) => escalation)[0] ?? null;

  const [deleteEscalation] = useDeleteEscalationMutation({
    refetchQueries: ["GetComplianceCase"],
    onError: (e) => {
      toast.error(e.message);
    },
  });

  const handleDelete = useCallback(async () => {
    if (escalationDetail) {
      await deleteEscalation({
        variables: {
          id: escalationDetail?.id,
        },
      });
    }
  }, [deleteEscalation, escalationDetail]);

  const getGPCount = useGetArenaCountQuery({
    variables: {
      profileId: complianceCase?.investingProfile?.id ?? 0,
      caseTypes: [CaseType.InvestorOnboarding, CaseType.Remediation],
    },
    skip: !complianceCase?.investingProfile?.id,
  });

  useEffect(() => {
    const cases = getGPCount.data?.complianceProfile?.complianceCases ?? [];
    const uniqueArenas = cases.filter(
      (item, index, self) =>
        item.complianceCase?.externalArena?.id !== undefined &&
        self.findIndex(
          (i) => i.complianceCase?.externalArena?.id === item.complianceCase?.externalArena?.id,
        ) === index,
    );
    const clients = getGPCount.data?.complianceProfile?.clients || [];

    if (setCount) {
      if (isReusableProfileEnabled) {
        setCount(clients.length);
      } else {
        setCount(uniqueArenas.length);
      }
    }
  }, [
    getGPCount.data?.complianceProfile?.clients,
    getGPCount.data?.complianceProfile?.complianceCases,
    isReusableProfileEnabled,
    setCount,
  ]);

  const rowData = useMemo(() => {
    if (!complianceCase?.escalations) return [];
    return complianceCase.escalations.map((escalation) => ({
      issue: escalation.description || "",
      supportingDocs: escalation.document?.id || "",
      delete: "",
    }));
  }, [complianceCase]);

  const IssueGridColumns: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "issue",
      headerName: "Issue",
      cellRendererParams: {
        innerRenderer: (params: ICellRendererParams) => {
          return (
            <Text data-testid="issueCell" className={styles.issue}>
              {params.value}
            </Text>
          );
        },
        suppressCount: true,
      },
      minWidth: 260,
      autoHeight: true,
      cellStyle: { alignContent: "center" },
    },
    {
      field: "supportingDocs",
      headerName: "Supporting Documents",
      cellRenderer: (params: ICellRendererParams) => {
        if (params.value) {
          return <Text variant="strong-md">Uploaded</Text>;
        } else {
          return <Text variant="strong-md">Not Uploaded</Text>;
        }
      },
      minWidth: 200,
      cellStyle: { alignContent: "center" },
    },
    {
      field: "delete",
      headerName: "",
      cellRenderer: () => (
        <Icon
          data-testid="delete-issue"
          className={styles.deleteIcon}
          onClick={handleDelete}
          type="trash"
        />
      ),
      hide: !allowDeleteEscalation(complianceCase, assigneeRole),
      maxWidth: 60,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      cellStyle: { alignContent: "center" },
    },
  ];

  const isMasterDetailEnabled = useMemo(() => {
    if (complianceCase?.caseType === CaseType.Escalation) {
      return true;
    } else if (
      (complianceCase?.caseType === CaseType.RecurringHit ||
        isStandaloneCaseType(complianceCase?.caseType as CaseType)) &&
      ![ComplianceCaseSubStatus.Invalid, ComplianceCaseSubStatus.Resolved].includes(
        complianceCase?.subStatus as ComplianceCaseSubStatus,
      )
    ) {
      return true;
    }
    return false;
  }, [complianceCase]);

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    masterDetail: isMasterDetailEnabled,
    detailCellRenderer: ExpandableTextPanel,
    detailCellRendererParams: {
      complianceCase,
    },
    detailRowAutoHeight: true,
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    rowHeight: 46,
    headerHeight: 48,
    groupDefaultExpanded: -1,
  };

  return (
    <Grid
      className={styles.gridContainer}
      gridOptions={gridOptions}
      rowData={rowData}
      columnDefs={IssueGridColumns}
      suppressCellFocus={true}
      domLayout="autoHeight"
      suppressMovableColumns={true}
    />
  );
};
